/**
 * FieldsManager — Main orchestrator component
 *
 * Architecture:
 *   FieldsManager.js  ← this file (state + DnD logic only)
 *   FMSidebar.js      ← left toolbox panel
 *   SectionCard.js    ← individual section with fields/table
 *   FieldRow.js       ← single field row inside a section
 *   TableColumnRow.js ← single column row inside a table section
 *   TablePreview.js   ← live WYSIWYG table preview
 *   OptionsEditor.js  ← drag-sortable options list for select fields
 *   Modals.js         ← PreviewModal, TemplatePickerModal, SchemaNameModal
 *   constants.js      ← FIELD_TYPES, SECTION_COLORS, PRESET_TEMPLATES
 *   utils.js          ← uid, createField, createSection, validateSchema, etc.
 */

import React, { useState, useCallback, useRef, useEffect } from 'react';
import {
  DndContext,
  DragOverlay,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  MeasuringStrategy,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Table2, LayoutList, Wand2, Sparkles,
} from 'lucide-react';

import {
  fetchFieldSchema,
  createFieldSchema,
  updateFieldSchema,
  clearCurrentSchema,
} from '../../store/slices/fieldsManagerSlice';
import { parametersAPI } from '../../services/api';
import { getLocalized } from '../../i18n/getLocalized';

// Helper: convert hex color to "r,g,b" for rgba() usage in CSS
function hexToRgb(hex) {
  const clean = hex.replace('#', '');
  const bigint = parseInt(clean.length === 3
    ? clean.split('').map((c) => c + c).join('')
    : clean, 16);
  return `${(bigint >> 16) & 255},${(bigint >> 8) & 255},${bigint & 255}`;
}

import { FIELD_TYPES } from './constants';
import {
  uid, createField, createColumn, createSection, fieldTypeLabel,
  DEFAULT_SCHEMA, validateSchema, deepCloneWithFreshIds, buildSavePayload,
} from './utils';
import { defaultScopeForUser } from '../../components/CompanyScopeSelect/CompanyScopeSelect';

// Inject fresh IDs into an AI-generated schema (backend strips ids)
function hydrateAISchema(aiSchema) {
  return {
    sections: (aiSchema.sections || []).map((s, si) => {
      const base = {
        id: uid(),
        title: s.title || 'Untitled Section',
        type: s.type === 'table' ? 'table' : 'fields',
        color: s.color || '#6366f1',
        collapsed: false,
        order: si,
      };
      if (base.type === 'fields') {
        base.fields = (s.fields || []).map((f, fi) => ({
          id: uid(),
          label: f.label || '',
          type: f.type || 'text',
          required: !!f.required,
          placeholder: f.placeholder || '',
          width: f.width || 'full',
          order: fi,
          options: (f.options || []).map((o) => ({ id: uid(), label: o.label || '' })),
        }));
        base.tableColumns = [];
      } else {
        base.tableColumns = (s.tableColumns || []).map((c, ci) => ({
          id: uid(),
          header: c.header || '',
          type: c.type || 'text',
          width: c.width || 'medium',
          required: !!c.required,
          order: ci,
          options: (c.options || []).map((o) => ({ id: uid(), label: o.label || '' })),
        }));
        base.fields = [];
      }
      return base;
    }),
  };
}

import FMSidebar from './FMSidebar';
import FMTopBar from './FMTopBar';
import InlineAddBar from './InlineAddBar';
import SectionCard from './SectionCard';
import SystemFieldsSection from './SystemFieldsSection';
import FieldDropZone from './FieldDropZone';
import FieldRow from './FieldRow';
import { PreviewModal, TemplatePickerModal, SchemaNameModal } from './Modals';
import AIAssistant from './AIAssistant';

import './FieldsManager.scss';

// ─── Drag overlay renderer ────────────────────────────────────────────────────

function DragOverlayContent({ activeId, activeDragData, schema, t, lang }) {
  if (!activeId) return null;

  // Sidebar field type being dragged
  if (activeId.startsWith('sidebar-field-')) {
    const type = activeId.replace('sidebar-field-', '');
    const item = FIELD_TYPES.find((f) => f.type === type);
    if (!item) return null;
    const Icon = item.icon;
    return (
      <div className="fmx-field-tile fmx-field-tile--drag" style={{ '--ft-color': item.color, '--ft-bg': item.bg }}>
        <span className="fmx-field-tile__icon"><Icon /></span>
        <span className="fmx-field-tile__label">{fieldTypeLabel(t, item)}</span>
      </div>
    );
  }

  // Sidebar section type being dragged
  if (activeId === 'sidebar-section-fields' || activeId === 'sidebar-section-table') {
    const isTable2= activeId === 'sidebar-section-table';
    const Icon = isTable2? Table2: LayoutList;
    return (
      <div className={`fmx-section-card fmx-section-card--drag ${isTable2? 'fmx-section-card--table' : 'fmx-section-card--fields'}`}>
        <span className="fmx-section-card__icon"><Icon /></span>
        <span className="fmx-section-card__body">
          <span className="fmx-section-card__title">
            {isTable2
              ? t('fieldsManager.sidebar.tableSection')
              : t('fieldsManager.sidebar.fieldsSection')}
          </span>
        </span>
      </div>
    );
  }

  // Section being dragged
  if (activeDragData?.type === 'section') {
    const section = schema.sections.find((s) => s.id === activeId);
    if (section) {
      return (
        <div className="fm-section fm-section--overlay" style={{ '--section-color': section.color }}>
          <div className="fm-section__header">
            <span className="fm-section__title-wrap">
              <span style={{ fontWeight: 600, fontSize: '0.9375rem', color: '#1e293b' }}>
                {getLocalized(section.title, lang)}
              </span>
            </span>
            <span className={`fm-section-type-badge fm-section-type-badge--${section.type}`}>
              {section.type === 'table'
                ? t('fieldsManager.sectionCard.table')
                : t('fieldsManager.sectionCard.fields')}
            </span>
          </div>
        </div>
      );
    }
  }

  // Field being dragged
  if (activeDragData?.type === 'field') {
    const section = schema.sections.find((s) => s.id === activeDragData.sectionId);
    const field = section?.fields?.find((f) => f.id === activeId);
    if (field) {
      return (
        <FieldRow
          field={field}
          sectionId={activeDragData.sectionId}
          onUpdate={() => {}}
          onRemove={() => {}}
          errors={{}}
          isDragOverlay
        />
      );
    }
  }

  return null;
}

// The schema *name* and *description* are admin metadata rendered as plain
// text in the top bar, so they are flattened for display. Everything inside
// `sections` (titles, labels, descriptions, placeholders, option labels) is
// left as-is — it may be a multilang map like { en, tr } and the builder edits
// those values directly via MultiLangInput.
function asText(v) {
  if (v == null) return '';
  if (typeof v === 'object') {
    return v.en ?? v.tr ?? Object.values(v).find((x) => typeof x === 'string') ?? '';
  }
  return v;
}

// Normalize structure only (never text) so older documents get the fields the
// builder expects.
function normalizeSection(s) {
  return {
    ...s,
    fields: (s.fields || []).map((f) => ({
      description: '',
      ...f,
      options: f.options || [],
    })),
    tableColumns: (s.tableColumns || []).map((c) => ({ ...c, options: c.options || [] })),
  };
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function FieldsManager() {
  const { t, i18n } = useTranslation();
  const { schemaId: routeSchemaId, contractTypeId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { currentSchema, saving } = useSelector((state) => state.fieldsManager);
  const authUser = useSelector((state) => state.auth?.user || null);

  // ── Tenant accent color ───────────────────────────────────────────────────
  const { branding } = useSelector((state) => state.settings);
  const accentColor = branding?.accentColor || '#6366f1';
  const accentRgb = hexToRgb(accentColor);

  // Opened from a contract type (/contract-types/:id/fields) rather than
  // directly by schema id: resolve the type's attached schema first, so the
  // builder lands on that type's *current* form instead of a blank canvas.
  const [linkedSchemaId, setLinkedSchemaId] = useState(null);
  const [resolvingType, setResolvingType] = useState(Boolean(contractTypeId));

  const schemaId = routeSchemaId || linkedSchemaId;
  const isEdit = Boolean(schemaId);

  // ── Schema state ──────────────────────────────────────────────────────────
  const [schema, setSchema] = useState(() => DEFAULT_SCHEMA());
  const [schemaName, setSchemaName] = useState('');
  const [schemaDescription, setSchemaDescription] = useState('');
  const [errors, setErrors] = useState({});
  const [unsaved, setUnsaved] = useState(false);

  // ── UI state ──────────────────────────────────────────────────────────────
  const [showPreview, setShowPreview] = useState(false);
  const [showTemplatePicker, setShowTemplatePicker] = useState(false);
  const [showNameModal, setShowNameModal] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  // Docked Sentry panel starts closed — the canvas is the landing experience.
  const [aiCollapsed, setAiCollapsed] = useState(true);

  // The builder opens straight onto the form canvas — the fixed core fields
  // plus whatever the schema already contains. AI stays available on demand
  // from the sidebar (docked assistant), never as a landing screen.

  // ── DnD state ─────────────────────────────────────────────────────────────
  const [activeId, setActiveId] = useState(null);
  const [activeDragData, setActiveDragData] = useState(null);

  // ── Resolve contract type → its attached schema ───────────────────────────
  useEffect(() => {
    if (!contractTypeId) return undefined;
    let cancelled = false;
    setResolvingType(true);
    parametersAPI.contractTypes.get(contractTypeId)
      .then(({ data }) => {
        if (cancelled) return;
        const item = data?.item;
        if (item?.fieldSchemaId) {
          // Populated document or raw ObjectId depending on the caller.
          setLinkedSchemaId(item.fieldSchemaId._id || item.fieldSchemaId);
        } else if (item?.name) {
          // No schema attached yet — start a new one named after the type.
          setSchemaName(asText(item.name));
        }
      })
      .catch(() => {
        if (!cancelled) toast.error(t('fieldsManager.failedToLoadType'));
      })
      .finally(() => { if (!cancelled) setResolvingType(false); });
    return () => { cancelled = true; };
  }, [contractTypeId, t]);

  // ── Load existing schema ──────────────────────────────────────────────────
  useEffect(() => {
    if (isEdit && schemaId) {
      dispatch(fetchFieldSchema(schemaId));
    }
    return () => { dispatch(clearCurrentSchema()); };
  }, [dispatch, schemaId, isEdit]);

  useEffect(() => {
    if (currentSchema && isEdit) {
      setSchemaName(asText(currentSchema.name));
      setSchemaDescription(asText(currentSchema.description));
      setSchema({
        sections: (currentSchema.sections || []).map(normalizeSection),
      });
      setUnsaved(false);
      setShowTemplatePicker(false);
    }
  }, [currentSchema, isEdit]);

  // ── Undo (Ctrl+Z) ─────────────────────────────────────────────────────────
  const historyRef = useRef([]);
  const historyIndexRef = useRef(-1);

  const pushHistory = useCallback((prev) => {
    const trimmed = historyRef.current.slice(0, historyIndexRef.current + 1);
    historyRef.current = [...trimmed, prev].slice(-20);
    historyIndexRef.current = historyRef.current.length - 1;
  }, []);

  const updateSchema = useCallback((updater) => {
    setSchema((prev) => {
      pushHistory(prev);
      const next = typeof updater === 'function' ? updater(prev) : updater;
      setUnsaved(true);
      return next;
    });
  }, [pushHistory]);

  const undo = useCallback(() => {
    if (historyIndexRef.current < 0) return;
    const prev = historyRef.current[historyIndexRef.current];
    if (!prev) return;
    historyIndexRef.current -= 1;
    setSchema(prev);
    setUnsaved(true);
    toast.info(t('fieldsManager.undo'), { autoClose: 1200 });
  }, []);

  useEffect(() => {
    const handler = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'z') { e.preventDefault(); undo(); }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [undo]);

  // ── Section operations ────────────────────────────────────────────────────
  const addSection = useCallback((type = 'fields') => {
    updateSchema((prev) => ({
      ...prev,
      sections: [...prev.sections, createSection(type, { order: prev.sections.length })],
    }));
  }, [updateSchema]);

  const updateSection = useCallback((sectionId, changes) => {
    updateSchema((prev) => ({
      ...prev,
      sections: prev.sections.map((s) => s.id === sectionId ? { ...s, ...changes } : s),
    }));
  }, [updateSchema]);

  const removeSection = useCallback((sectionId) => {
    updateSchema((prev) => ({
      ...prev,
      // Standard sections cannot be deleted — only set passive.
      sections: prev.sections.filter((s) => s.id !== sectionId || s.isStandard),
    }));
  }, [updateSchema]);

  const duplicateSection = useCallback((sectionId) => {
    updateSchema((prev) => {
      const idx = prev.sections.findIndex((s) => s.id === sectionId);
      if (idx === -1) return prev;
      const original = prev.sections[idx];
      const copy = deepCloneWithFreshIds({ ...original, title: `${original.title} (Copy)` });
      // A duplicated section is a plain user section — deletable, editable.
      delete copy.isStandard;
      if (Array.isArray(copy.fields)) copy.fields = copy.fields.map(({ isStandard, ...f }) => f);
      const sections = [...prev.sections];
      sections.splice(idx + 1, 0, copy);
      return { ...prev, sections };
    });
  }, [updateSchema]);

  // ── Field operations ──────────────────────────────────────────────────────
  const addField = useCallback((sectionId, type = 'text') => {
    updateSchema((prev) => ({
      ...prev,
      sections: prev.sections.map((s) => {
        if (s.id !== sectionId) return s;
        return { ...s, fields: [...s.fields, createField(type, { order: s.fields.length })] };
      }),
    }));
  }, [updateSchema]);

  // Insert a new field at an exact position — `index` null means append.
  const addFieldAt = useCallback((sectionId, type, index) => {
    updateSchema((prev) => ({
      ...prev,
      sections: prev.sections.map((s) => {
        if (s.id !== sectionId || s.type !== 'fields') return s;
        const fields = [...s.fields];
        const at = index == null || index < 0 || index > fields.length ? fields.length : index;
        fields.splice(at, 0, createField(type, { order: at }));
        return { ...s, fields };
      }),
    }));
  }, [updateSchema]);

  // No editable section on the canvas yet → make one and seed it with the
  // dropped field, so a library drag is never a no-op.
  const addSectionWithField = useCallback((type) => {
    updateSchema((prev) => {
      const section = createSection('fields', { order: prev.sections.length });
      section.fields = [createField(type, { order: 0 })];
      return { ...prev, sections: [...prev.sections, section] };
    });
  }, [updateSchema]);

  const updateField = useCallback((sectionId, fieldId, changes) => {
    updateSchema((prev) => ({
      ...prev,
      sections: prev.sections.map((s) => {
        if (s.id !== sectionId) return s;
        return { ...s, fields: s.fields.map((f) => f.id === fieldId ? { ...f, ...changes } : f) };
      }),
    }));
  }, [updateSchema]);

  const removeField = useCallback((sectionId, fieldId) => {
    updateSchema((prev) => ({
      ...prev,
      sections: prev.sections.map((s) => {
        if (s.id !== sectionId) return s;
        // Standard fields cannot be deleted — only set passive.
        return { ...s, fields: s.fields.filter((f) => f.id !== fieldId || f.isStandard) };
      }),
    }));
  }, [updateSchema]);

  // ── Column operations ─────────────────────────────────────────────────────
  const addColumn = useCallback((sectionId) => {
    updateSchema((prev) => ({
      ...prev,
      sections: prev.sections.map((s) => {
        if (s.id !== sectionId) return s;
        const cols = s.tableColumns || [];
        return { ...s, tableColumns: [...cols, createColumn({ order: cols.length })] };
      }),
    }));
  }, [updateSchema]);

  const updateColumn = useCallback((sectionId, colId, changes) => {
    updateSchema((prev) => ({
      ...prev,
      sections: prev.sections.map((s) => {
        if (s.id !== sectionId) return s;
        return { ...s, tableColumns: (s.tableColumns || []).map((c) => c.id === colId ? { ...c, ...changes } : c) };
      }),
    }));
  }, [updateSchema]);

  const removeColumn = useCallback((sectionId, colId) => {
    updateSchema((prev) => ({
      ...prev,
      sections: prev.sections.map((s) => {
        if (s.id !== sectionId) return s;
        return { ...s, tableColumns: (s.tableColumns || []).filter((c) => c.id !== colId) };
      }),
    }));
  }, [updateSchema]);

  // ── AI-generated schema application ───────────────────────────────────────
  const handleAISchemaApply = useCallback((aiSchema) => {
    if (!aiSchema || !Array.isArray(aiSchema.sections)) return;
    // Hydrate with fresh IDs on every section/field/column/option
    const hydrated = hydrateAISchema(aiSchema);
    updateSchema(() => hydrated);
    toast.success(t('fieldsManager.aiSchemaApplied'), { autoClose: 2500 });
  }, [updateSchema]);

  // ── Template selection ────────────────────────────────────────────────────
  const handleTemplateSelect = useCallback((template) => {
    const freshSchema = deepCloneWithFreshIds(template.schema);
    setSchema(freshSchema);
    if (template.name !== 'Start from Scratch') {
      setSchemaName(`${template.name} Schema`);
    }
    setUnsaved(true);
    setShowTemplatePicker(false);
    toast.success(t('fieldsManager.templateLoaded', { name: template.name }), { autoClose: 2000 });
  }, []);

  // ── Save ──────────────────────────────────────────────────────────────────
  const handleSave = useCallback(async (nameData) => {
    const errs = validateSchema(schema);
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      toast.error(t('fieldsManager.fixErrors', { count: Object.keys(errs).length }), { autoClose: 4000 });
      return;
    }
    setErrors({});

    const finalName = nameData?.name || schemaName;
    const finalDescription = nameData?.description || schemaDescription;

    if (!finalName.trim()) {
      setShowNameModal(true);
      return;
    }

    const payload = buildSavePayload(finalName, finalDescription, schema);

    try {
      if (isEdit && schemaId) {
        await dispatch(updateFieldSchema({ id: schemaId, data: payload })).unwrap();
        toast.success(t('fieldsManager.schemaUpdated'));
      } else {
        // A new schema starts at its creator's scope authority: All Companies
        // for owners/admins, the creator's own company otherwise. (Scope is
        // managed later from the schema list's scope dialog.)
        const result = await dispatch(createFieldSchema({ ...payload, companyIds: defaultScopeForUser(authUser) })).unwrap();
        const newId = result.item._id;
        if (contractTypeId) {
          // Attach the new schema to the contract type it was built for,
          // and stay on the contract-type route.
          await parametersAPI.contractTypes.update(contractTypeId, { fieldSchemaId: newId });
          setLinkedSchemaId(newId);
        } else {
          navigate(`/fields-manager/${newId}`, { replace: true });
        }
        toast.success(t('fieldsManager.schemaSaved'));
      }
      setSchemaName(finalName.trim());
      setSchemaDescription(finalDescription.trim());
      setUnsaved(false);
      setShowNameModal(false);
    } catch (err) {
      toast.error(err || t('fieldsManager.failedToSave'));
    }
  }, [schema, schemaName, schemaDescription, isEdit, schemaId, contractTypeId, authUser, dispatch, navigate, t]);

  const handleSaveClick = useCallback(() => {
    if (!schemaName.trim()) setShowNameModal(true);
    else handleSave();
  }, [schemaName, handleSave]);

  // ── DnD ───────────────────────────────────────────────────────────────────
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));
  const sectionIds = schema.sections.map((s) => s.id);

  const handleDragStart = ({ active }) => {
    setActiveId(active.id);
    setActiveDragData(active.data.current);
  };

  const handleDragEnd = ({ active, over }) => {
    setActiveId(null);
    setActiveDragData(null);
    if (!over) return;

    const activeData = active.data.current || {};
    const overData = over.data.current || {};

    // ── Drop sidebar section type → add section ──
    if (active.id === 'sidebar-section-fields') { addSection('fields'); return; }
    if (active.id === 'sidebar-section-table') { addSection('table'); return; }

    // ── Drop sidebar field type → insert field at the hovered position ──
    if (active.id.startsWith('sidebar-field-')) {
      const fieldType = active.id.replace('sidebar-field-', '');

      // Resolve the target section and the index to insert at. Hovering an
      // existing field inserts *before* it; the section's drop zone appends.
      let targetSectionId = null;
      let insertIndex = null;

      if (overData.type === 'dropzone') {
        targetSectionId = overData.sectionId;
      } else if (overData.type === 'field') {
        targetSectionId = overData.sectionId;
        const target = schema.sections.find((s) => s.id === targetSectionId);
        const idx = target?.fields?.findIndex((f) => f.id === over.id);
        if (idx != null && idx >= 0) insertIndex = idx;
      } else if (overData.type === 'section') {
        targetSectionId = over.id;
      } else {
        const section = schema.sections.find(
          (s) => s.id === over.id || (s.fields && s.fields.some((f) => f.id === over.id))
        );
        if (section) targetSectionId = section.id;
      }

      const targetSection = schema.sections.find((s) => s.id === targetSectionId);
      if (targetSection && targetSection.type === 'fields') {
        addFieldAt(targetSectionId, fieldType, insertIndex);
      } else if (!schema.sections.some((s) => s.type === 'fields')) {
        // Nothing editable to drop into yet — create the first section for them.
        addSectionWithField(fieldType);
      }
      return;
    }

    // ── Move an existing field onto a section's drop zone → append there ──
    if (activeData.type === 'field' && overData.type === 'dropzone') {
      const fromSectionId = activeData.sectionId;
      const toSectionId = overData.sectionId;
      updateSchema((prev) => {
        let moved = null;
        const stripped = prev.sections.map((s) => {
          if (s.id !== fromSectionId) return s;
          moved = s.fields.find((f) => f.id === active.id);
          return { ...s, fields: s.fields.filter((f) => f.id !== active.id) };
        });
        if (!moved) return prev;
        return {
          ...prev,
          sections: stripped.map((s) => (
            s.id === toSectionId && s.type === 'fields'
              ? { ...s, fields: [...s.fields, moved] }
              : s
          )),
        };
      });
      return;
    }

    // ── Reorder sections ──
    if (activeData.type === 'section' && overData.type === 'section') {
      const oldIdx = schema.sections.findIndex((s) => s.id === active.id);
      const newIdx = schema.sections.findIndex((s) => s.id === over.id);
      if (oldIdx !== newIdx) {
        updateSchema((prev) => ({ ...prev, sections: arrayMove(prev.sections, oldIdx, newIdx) }));
      }
      return;
    }

    // ── Reorder fields within same section ──
    if (activeData.type === 'field' && overData.type === 'field' && activeData.sectionId === overData.sectionId) {
      const sectionId = activeData.sectionId;
      updateSchema((prev) => ({
        ...prev,
        sections: prev.sections.map((s) => {
          if (s.id !== sectionId) return s;
          const oldIdx = s.fields.findIndex((f) => f.id === active.id);
          const newIdx = s.fields.findIndex((f) => f.id === over.id);
          return { ...s, fields: arrayMove(s.fields, oldIdx, newIdx) };
        }),
      }));
      return;
    }

    // ── Move field between sections ──
    if (activeData.type === 'field' && activeData.sectionId !== (overData.sectionId || over.id)) {
      const fromSectionId = activeData.sectionId;
      const toSectionId = overData.sectionId || over.id;
      const toSection = schema.sections.find((s) => s.id === toSectionId);
      if (!toSection || toSection.type !== 'fields') return;

      updateSchema((prev) => {
        // Standard fields stay in their standard section — no cross-section move.
        const fromSection = prev.sections.find((s) => s.id === fromSectionId);
        const candidate = fromSection?.fields?.find((f) => f.id === active.id);
        if (candidate?.isStandard) return prev;
        let movedField = null;
        const sections = prev.sections.map((s) => {
          if (s.id === fromSectionId) {
            movedField = s.fields.find((f) => f.id === active.id);
            return { ...s, fields: s.fields.filter((f) => f.id !== active.id) };
          }
          return s;
        });
        if (!movedField) return prev;
        return {
          ...prev,
          sections: sections.map((s) => {
            if (s.id === toSectionId) {
              const insertIdx = overData.type === 'field'
                ? s.fields.findIndex((f) => f.id === over.id)
                : s.fields.length;
              const newFields = [...s.fields];
              newFields.splice(insertIdx === -1 ? newFields.length : insertIdx, 0, movedField);
              return { ...s, fields: newFields };
            }
            return s;
          }),
        };
      });
      return;
    }

    // ── Reorder table columns ──
    if (activeData.type === 'tableCol' && overData.type === 'tableCol' && activeData.sectionId === overData.sectionId) {
      const sectionId = activeData.sectionId;
      updateSchema((prev) => ({
        ...prev,
        sections: prev.sections.map((s) => {
          if (s.id !== sectionId) return s;
          const oldIdx = s.tableColumns.findIndex((c) => c.id === active.id);
          const newIdx = s.tableColumns.findIndex((c) => c.id === over.id);
          return { ...s, tableColumns: arrayMove(s.tableColumns, oldIdx, newIdx) };
        }),
      }));
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div
      className="fm-root"
      style={{
        '--fm-accent': accentColor,
        '--fm-accent-rgb': accentRgb,
        '--fm-accent-bg': `rgba(${accentRgb}, 0.08)`,
        '--fm-accent-light': `rgba(${accentRgb}, 0.15)`,
        '--fm-accent-shadow': `rgba(${accentRgb}, 0.35)`,
      }}
    >
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        measuring={{ droppable: { strategy: MeasuringStrategy.Always } }}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        {/* ── Sidebar ── */}
        <FMSidebar
          collapsed={sidebarCollapsed}
          onToggleCollapse={() => setSidebarCollapsed((v) => !v)}
          onShowTemplatePicker={() => setShowTemplatePicker(true)}
          onShowPreview={() => setShowPreview(true)}
          onAskAI={() => setAiCollapsed(false)}
          onAddFieldToFirstSection={(type) => {
            const firstFieldSection = schema.sections.find((s) => s.type === 'fields');
            if (firstFieldSection) addField(firstFieldSection.id, type);
            else addSectionWithField(type);
          }}
        />

        {/* ── Canvas ── */}
        <main className="fm-canvas">
          <div className="fmx-canvas">
            <FMTopBar
              schemaName={schemaName}
              onEditName={() => setShowNameModal(true)}
              unsaved={unsaved}
              saving={saving}
              sectionCount={schema.sections.length}
              fieldCount={schema.sections.reduce(
                (acc, s) => acc + (s.type === 'table' ? (s.tableColumns?.length || 0) : (s.fields?.length || 0)),
                0,
              )}
              onBack={() => navigate(-1)}
              onPreview={() => setShowPreview(true)}
              onSave={handleSaveClick}
            />

            {/* Document-style canvas */}
            <div className="fmx-doc">
              <div className="fmx-doc__inner">
                {/* Fixed core fields — always visible, never editable */}
                <SystemFieldsSection />

                {resolvingType ? (
                  <div className="fmx-empty fmx-empty--compact">
                    <p className="fmx-empty__desc">{t('common.loading')}</p>
                  </div>
                ) : schema.sections.length === 0 ? (
                  <div className="fmx-empty fmx-empty--compact">
                    <h2 className="fmx-empty__title">{t('fieldsManager.emptyCanvas.title')}</h2>
                    <p className="fmx-empty__desc">{t('fieldsManager.emptyCanvas.desc')}</p>

                    {/* Canvas-level drop target so a library drag always lands */}
                    <FieldDropZone sectionId="__new__" alwaysVisible />

                    <div className="fmx-empty__row">
                      <button
                        type="button"
                        className="fmx-action-mini"
                        onClick={() => addSection('fields')}
                      >
                        <LayoutList />
                        <span>{t('fieldsManager.emptyCanvas.addFieldsSection')}</span>
                      </button>
                      <button
                        type="button"
                        className="fmx-action-mini"
                        onClick={() => addSection('table')}
                      >
                        <Table2 />
                        <span>{t('fieldsManager.emptyCanvas.addTableSection')}</span>
                      </button>
                      <button
                        type="button"
                        className="fmx-action-mini"
                        onClick={() => setShowTemplatePicker(true)}
                      >
                        <Wand2 />
                        <span>{t('fieldsManager.emptyCanvas.browseTemplates')}</span>
                      </button>
                      <button
                        type="button"
                        className="fmx-action-mini fmx-action-mini--ai"
                        onClick={() => setAiCollapsed(false)}
                      >
                        <Sparkles />
                        <span>{t('fieldsManager.emptyCanvas.askAI')}</span>
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <InlineAddBar
                      variant="between"
                      onAddFields={() => {
                        // Insert at top — duplicate addSection behavior but at index 0
                        updateSchema((prev) => ({
                          ...prev,
                          sections: [createSection('fields', { order: 0 }), ...prev.sections],
                        }));
                      }}
                      onAddTable={() => {
                        updateSchema((prev) => ({
                          ...prev,
                          sections: [createSection('table', { order: 0 }), ...prev.sections],
                        }));
                      }}
                      onOpenTemplates={() => setShowTemplatePicker(true)}
                      onAskAI={() => setAiCollapsed(false)}
                    />
                    <SortableContext items={sectionIds} strategy={verticalListSortingStrategy}>
                      {schema.sections.map((section, idx) => (
                        <React.Fragment key={section.id}>
                          <SectionCard
                            section={section}
                            onUpdate={updateSection}
                            onRemove={removeSection}
                            onDuplicate={duplicateSection}
                            onAddField={addField}
                            onAddColumn={addColumn}
                            onUpdateField={updateField}
                            onRemoveField={removeField}
                            onUpdateColumn={updateColumn}
                            onRemoveColumn={removeColumn}
                            errors={errors}
                          />
                          <InlineAddBar
                            variant={idx === schema.sections.length - 1 ? 'end' : 'between'}
                            label={idx === schema.sections.length - 1
                              ? t('fieldsManager.inlineAdd.addAnotherSection')
                              : undefined}
                            onAddFields={() => {
                              updateSchema((prev) => {
                                const sections = [...prev.sections];
                                sections.splice(idx + 1, 0, createSection('fields', { order: idx + 1 }));
                                return { ...prev, sections };
                              });
                            }}
                            onAddTable={() => {
                              updateSchema((prev) => {
                                const sections = [...prev.sections];
                                sections.splice(idx + 1, 0, createSection('table', { order: idx + 1 }));
                                return { ...prev, sections };
                              });
                            }}
                            onOpenTemplates={() => setShowTemplatePicker(true)}
                            onAskAI={() => setAiCollapsed(false)}
                          />
                        </React.Fragment>
                      ))}
                    </SortableContext>
                  </>
                )}
              </div>
            </div>
          </div>
        </main>

        {/* ── Docked AI Assistant — opt-in, opened from the sidebar ── */}
        <AIAssistant
          docked
          collapsed={aiCollapsed}
          onToggleCollapse={() => setAiCollapsed((v) => !v)}
          onApplySchema={handleAISchemaApply}
          currentSchema={schema}
        />

        {/* Drag overlay */}
        <DragOverlay dropAnimation={{ duration: 200, easing: 'cubic-bezier(0.18, 0.67, 0.6, 1.22)' }}>
          <DragOverlayContent activeId={activeId} activeDragData={activeDragData} schema={schema} t={t} lang={i18n.language} />
        </DragOverlay>
      </DndContext>

      {/* Modals */}
      {showPreview && (
        <PreviewModal
          schema={schema}
          schemaName={schemaName || t('fieldsManager.untitledSchema')}
          onClose={() => setShowPreview(false)}
        />
      )}
      {showTemplatePicker && (
        <TemplatePickerModal
          onSelect={handleTemplateSelect}
          onClose={() => setShowTemplatePicker(false)}
        />
      )}
      {showNameModal && (
        <SchemaNameModal
          initialName={schemaName}
          onConfirm={handleSave}
          onClose={() => setShowNameModal(false)}
        />
      )}

    </div>
  );
}
